import React, { useEffect, useState, VoidFunctionComponent } from "react";
import { RatingBar } from "@/components/responsive/product/layer/rating/RatingBar";
import { RatingDetails } from "@/components/responsive/product/layer/rating/RatingDetails";
import { ReviewBlock } from "@/components/responsive/product/layer/rating/ReviewBlock";
import { Review } from "@/models/scope/productSheet/reviews/Review";
import { Select } from "@/components/responsive/select/Select";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from '@/models/appState/RootState';
import { fetchReviewsListData } from "@/redux/reducers/responsive/product/reviewsAction";
import { ProductReviewListStatistics } from "@/models/scope/productSheet/reviews/ProductReviewListStatistics";

interface Props {
    reviewListStatistics: ProductReviewListStatistics;
    reviews: Review[]
    productId: string
}

const INITIAL_COUNT = 0;
const RATING_STEP = 5;

const getInitialCount = (count: string) => {
    const totalNumber = parseInt(count);
    if (!totalNumber) {
        return 0;
    }
    if (totalNumber > RATING_STEP) {
        return RATING_STEP;
    }
    return totalNumber;
};

const updateCount = (initialValue: number, totalValue: number) => {
    if (!initialValue || !totalValue) {
        return 0;
    }
    if (initialValue + RATING_STEP < totalValue) {
        return initialValue + RATING_STEP;
    }
    return totalValue;
};

export const RatingLayer: VoidFunctionComponent<Props> = ({ productId, reviewListStatistics, reviews }) => {
    const [numberOfReviewToDisplay, setNumberOfReviewToDisplay] = useState(INITIAL_COUNT);
    const dispatch = useDispatch();
    const reviewsData = useSelector((state: RootState) => state.reviews.reviews.items);
    const pageIndex = useSelector((state: RootState) => state.reviews.pageIndex);
    const [sortId, setSortId] = useState('')
    const changeFromClient = useSelector((state: RootState) => state.reviews.changeFromClient);
    const productReviewsListData: Review[] = changeFromClient ? reviewsData : reviews;
    const { total } = reviewListStatistics;

    useEffect(() => {
        setNumberOfReviewToDisplay(getInitialCount(total));
    }, [total, sortId]);

    const handleDisplayMoreRating = () => {
        setNumberOfReviewToDisplay(updateCount(numberOfReviewToDisplay, parseInt(total)));
        dispatch(fetchReviewsListData({
            productId: productId,
            pageIndex: pageIndex + 1,
            productReviewsListItems: productReviewsListData,
            sortedBy: sortId,
        }));
    };

    const handleSortRating = (newSortId: string) => {
        setSortId(newSortId);
        dispatch(fetchReviewsListData({
            productId: productId,
            pageIndex: 1,
            productReviewsListItems: [],
            sortedBy: newSortId,
        }));
    };
    const sortReviews = () => {
        const optionsList = [
            {
                "id": "DateDesc",
                "label": "Du plus récent au plus ancien",
            },
            {
                "id": "DateAsc",
                "label": "Du plus ancien au plus récent",
            },
            {
                "id": "RateDesc",
                "label": "De la meilleure note à la moins bonne note",
            },
            {
                "id": "RateAsc",
                "label": "De la moins bonne note à la meilleure note",
            }
        ]
        return (
            <>
                <label>Trier par</label>
                <Select
                    inputList={optionsList}
                    value={'DateDesc'}
                    placeholder={'Faites votre choix'}
                    callBack={handleSortRating}
                />
            </>
        )
    }
    const renderBottom = () => {
        return (
            <div className='bottom'>
                <p>
                    Vous avez vu {numberOfReviewToDisplay} avis sur {total || 0}
                </p>

                <RatingBar
                    count={String(numberOfReviewToDisplay)}
                    totalCount={total || '0'}
                    reviewsProgression
                />
                {numberOfReviewToDisplay < parseInt(total) && (
                    <div
                        className='bottom-button'
                        onClick={() => handleDisplayMoreRating()}
                    >
                        Voir plus
                    </div>
                )}
            </div>
        );
    };
    const renderReviewTitle = () => {
        return (
            <div className='review-title'>
                {sortReviews()}
                <p>
                    {numberOfReviewToDisplay} avis sur {total || 0}
                </p>
            </div>
        );
    };

    return (
        <div className='rating-layer'>
            <RatingDetails reviewListStatistics={reviewListStatistics} />
            {renderReviewTitle()}
            {productReviewsListData?.map(
                (review, index) =>
                    index < numberOfReviewToDisplay && (
                        <ReviewBlock review={review} key={index} />
                    ),
            )}
            {renderBottom()}
        </div>
    );
};